import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { MetadataAuditComparerRow } from "../../api/metadataAuditComparerApi";
import { apiService } from "../../api/api";
import { ProgressSpinner } from "primereact/progressspinner";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Card } from "primereact/card";
import {
  identifiedIssuesBody,
  dataHygieneAlertBody,
  manualReviewRequiredBody,
  tokenBodyFor,
  severityRowClassNames,
  toCellTokens,
  labelForToken,
  W,
  WRAP_PRELINE,
  formatStringField,
} from "./MetadataAuditComparer.renderers";
import "./MetadataAuditComparer.css";
import { FilterService } from "primereact/api";
import { MAIN_SEARCH_FIELDS, FilterBar } from "./MetadataAuditComparer.filters";
import { ColumnDef, MainSearchField, TokenizedField } from "./MetadataAuditComparer.types";
import { trackToolAction } from "../../utils/analytics";
import { FULL_PAGE_TABLE_SCROLL_HEIGHT } from "../../config/tableLayout";

const PAGE_SIZE = 50;

const normalizeTokens = (v: unknown): string[] =>
  String(v ?? "")
    .split(/[|,;]/g)
    .map((x) => x.trim().toLowerCase())
    .filter(Boolean);

const normalizeFilter = (v: unknown): string[] =>
  Array.isArray(v)
    ? v.map((x) => String(x).trim().toLowerCase()).filter(Boolean)
    : [];

FilterService.register("tokenAny", (value, filter) => {
  const selected = normalizeFilter(filter);
  if (!selected.length) return true;
  const tokens = normalizeTokens(value);
  return selected.some((s) => tokens.includes(s));
});

FilterService.register("containsAny", (value, filter) => {
  const selected = normalizeFilter(filter);
  if (!selected.length) return true;
  const haystack = String(value ?? "").toLowerCase();
  return selected.some((entry) => haystack.includes(entry));
});

const MetadataAuditComparer = () => {
  const [rows, setRows] = useState<MetadataAuditComparerRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sortBy, setSortBy] = useState<"assetKey" | "MatchConfidence">(
    "assetKey",
  );
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [queryFilters, setQueryFilters] = useState({
    hasDiscrepancy: null as boolean | null,
    minMatchConfidence: undefined as number | undefined,
    maxMatchConfidence: undefined as number | undefined,
  });
  const [tableFilters, setTableFilters] = useState<any>({
    Brand: { value: [], matchMode: "containsAny" },
    CustomId: { value: [], matchMode: "containsAny" },
    MCTitle: { value: [], matchMode: "containsAny" },
    CompTitle: { value: [], matchMode: "containsAny" },
    SRTitle: { value: [], matchMode: "containsAny" },
    MCISWC: { value: [], matchMode: "containsAny" },
    CompISWC: { value: [], matchMode: "containsAny" },
    MCWriters: { value: [], matchMode: "containsAny" },
    CompWriter: { value: [], matchMode: "containsAny" },
    SRISRC: { value: [], matchMode: "containsAny" },
    SRArtist: { value: [], matchMode: "containsAny" },
    SRAssetId: { value: [], matchMode: "containsAny" },
    CompAssetId: { value: [], matchMode: "containsAny" },
    UMPGInternalWorkCode: { value: [], matchMode: "containsAny" },
    IdentifiedIssues: { value: [], matchMode: "containsAny" },

    DiscrepancyTypes: { value: null, matchMode: "tokenAny" },
    HygieneTypes: { value: null, matchMode: "tokenAny" },
    DiscrepancySeverity: { value: null, matchMode: "tokenAny" },

    DataHygieneAlert: { value: null, matchMode: "equals" },
    ManualReviewRequired: { value: true, matchMode: "equals" },
  });

  const discrepancyViewOptions: Array<{
    label: string;
    value: boolean | null;
  }> = [
    { label: "All", value: null },
    { label: "Has Discrepancy", value: true },
    { label: "No Discrepancy", value: false },
  ];

  const handleFieldFilter = (field: string, value: any, matchMode?: string) => {
    const nextMode = matchMode ?? tableFilters[field]?.matchMode ?? "contains";
    const normalizedValue =
      nextMode === "equals" && value === undefined ? null : value;
    setTableFilters((prev: any) => ({
      ...prev,
      [field]: { value: normalizedValue, matchMode: nextMode },
    }));
  };

  const [multiDrafts, setMultiDrafts] = useState<Record<MainSearchField, string>>(
    () =>
      Object.fromEntries(
        MAIN_SEARCH_FIELDS.map(({ field }) => [field, ""]),
      ) as Record<MainSearchField, string>,
  );

  const handleMultiDraftChange = (field: MainSearchField, value: string) => {
    setMultiDrafts((prev) => ({ ...prev, [field]: value }));
  };

  const handleMultiAdd = (field: MainSearchField, rawValue?: string) => {
    const candidate = (rawValue ?? multiDrafts[field]).trim();
    if (!candidate) return;

    const parsedTerms = candidate
      .split(/[,\n]/g)
      .map((item) => item.trim())
      .filter(Boolean);

    if (!parsedTerms.length) return;

    const existingTerms = Array.isArray(tableFilters[field]?.value)
      ? tableFilters[field].value
      : [];
    const mergedTerms = Array.from(new Set([...existingTerms, ...parsedTerms]));

    handleFieldFilter(field, mergedTerms, "containsAny");
    setMultiDrafts((prev) => ({ ...prev, [field]: "" }));
  };

  const handleMultiRemove = (field: MainSearchField, chipValue: string) => {
    const existingTerms = Array.isArray(tableFilters[field]?.value)
      ? tableFilters[field].value
      : [];
    const nextTerms = existingTerms.filter((entry: string) => entry !== chipValue);
    handleFieldFilter(field, nextTerms, "containsAny");
  };

  const handleMultiKeyDown = (
    field: MainSearchField,
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      handleMultiAdd(field);
    }
  };

  const handleMultiClear = (field: MainSearchField) => {
    handleFieldFilter(field, [], "containsAny");
    setMultiDrafts((prev) => ({ ...prev, [field]: "" }));
  };

  const handleResetFilters = () => {
    setTableFilters({
      Brand: { value: [], matchMode: "containsAny" },
      CustomId: { value: [], matchMode: "containsAny" },
      MCTitle: { value: [], matchMode: "containsAny" },
      CompTitle: { value: [], matchMode: "containsAny" },
      SRTitle: { value: [], matchMode: "containsAny" },
      MCISWC: { value: [], matchMode: "containsAny" },
      CompISWC: { value: [], matchMode: "containsAny" },
      MCWriters: { value: [], matchMode: "containsAny" },
      CompWriter: { value: [], matchMode: "containsAny" },
      SRISRC: { value: [], matchMode: "containsAny" },
      SRArtist: { value: [], matchMode: "containsAny" },
      SRAssetId: { value: [], matchMode: "containsAny" },
      CompAssetId: { value: [], matchMode: "containsAny" },
      UMPGInternalWorkCode: { value: [], matchMode: "containsAny" },
      IdentifiedIssues: { value: [], matchMode: "containsAny" },
      DiscrepancyTypes: { value: null, matchMode: "tokenAny" },
      HygieneTypes: { value: null, matchMode: "tokenAny" },
      DiscrepancySeverity: { value: null, matchMode: "tokenAny" },
      DataHygieneAlert: { value: null, matchMode: "equals" },
      ManualReviewRequired: { value: true, matchMode: "equals" },
    });
    setQueryFilters({
      hasDiscrepancy: null,
      minMatchConfidence: undefined,
      maxMatchConfidence: undefined,
    });
    setMultiDrafts(
      Object.fromEntries(
        MAIN_SEARCH_FIELDS.map(({ field }) => [field, ""]),
      ) as Record<MainSearchField, string>,
    );
    setVisibleColumns(
      COLUMN_DEFS.map((col) => col.field).filter(
        (value) => !DEFAULT_HIDDEN_COLUMNS.has(value),
      ),
    );
  };

  const updateMatchConfidenceRange = (
    key: "minMatchConfidence" | "maxMatchConfidence",
    value: number | null,
  ) => {
    setQueryFilters((prev) => {
      const safe =
        value == null ? undefined : Math.min(1, Math.max(0, Number(value)));

      const next = { ...prev, [key]: safe };

      if (
        next.minMatchConfidence !== undefined &&
        next.maxMatchConfidence !== undefined &&
        next.minMatchConfidence > next.maxMatchConfidence
      ) {
        if (key === "minMatchConfidence") {
          next.maxMatchConfidence = next.minMatchConfidence;
        } else {
          next.minMatchConfidence = next.maxMatchConfidence;
        }
      }

      if (next.minMatchConfidence === 0) next.minMatchConfidence = undefined;
      if (next.maxMatchConfidence === 1) next.maxMatchConfidence = undefined;

      return next;
    });
  };

  const dt = useRef<DataTable<any>>(null);
  const now = new Date();
  const dtName = `composition-metadata-audit-${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const exportFilteredCsv = () => {
    trackToolAction("DataHealthTool", "export");
    dt.current?.exportCSV();
  };

  const tokenFilterOptionsFields = useMemo(() => {
    const buildOptionsForField = (field: TokenizedField) => {
      const unique = new Set<string>();
      rows.forEach((row) =>
        toCellTokens(row[field]).forEach((t) => unique.add(t)),
      );
      return Array.from(unique)
        .sort((a, b) => {
          return labelForToken(a).localeCompare(labelForToken(b));
        })
        .map((value) => ({ label: labelForToken(value), value }));
    };

    return {
      DiscrepancyTypes: buildOptionsForField("DiscrepancyTypes"),
      HygieneTypes: buildOptionsForField("HygieneTypes"),
      DiscrepancySeverity: buildOptionsForField("DiscrepancySeverity"),
    };
  }, [rows]);

  const COLUMN_DEFS: ColumnDef[] = [
    {field: "Brand", header: "Brand", filter: true, filterMatchMode: "containsAny", style: W.m},
    {field: "CustomId", header: "Custom ID", filter: true, filterMatchMode: "containsAny", style: W.s},
    {field: "MCTitle", header: "MC Title", filter: true, filterMatchMode: "containsAny", style: W.m},
    {field: "CompTitle", header: "Comp Title", filter: true, filterMatchMode: "containsAny", style: W.m},
    {field: "SRTitle", header: "SR Title", filter: true, filterMatchMode: "containsAny", style: W.m},
    {field: "MCISWC", header: "MC ISWC", filter: true, filterMatchMode: "containsAny", style: W.s},
    {field: "CompISWC", header: "Comp ISWC", filter: true, filterMatchMode: "containsAny", style: W.s},
    {field: "MCWriters", header: "MC Writer", filter: true, filterMatchMode: "containsAny", style: W.m},
    {field: "CompWriter", header: "Comp Writer", filter: true, filterMatchMode: "containsAny", style: W.m},
    {field: "SRISRC", header: "SR ISRC", filter: true, filterMatchMode: "containsAny", style: W.m},
    {field: "SRArtist", header: "SR Artist", filter: true, filterMatchMode: "containsAny", style: W.m},
    {field: "SRAssetId", header: "SR Asset ID", filter: true, filterMatchMode: "containsAny", style: W.m, bodyStyle: WRAP_PRELINE},
    {field: "CompAssetId", header: "Comp Asset ID", filter: true, filterMatchMode: "containsAny", style: W.m, bodyStyle: WRAP_PRELINE},
    {field: "UMPGInternalWorkCode", header: "UMPG Internal Work Code", filter: true, filterMatchMode: "containsAny", style: W.xs},
    {
      field: "IdentifiedIssues",
      header: "Identified Issues",
      filter: true,
      filterMatchMode: "containsAny",
      style: W.xl,
      bodyStyle: WRAP_PRELINE,
      body: identifiedIssuesBody,
    },
    {
      field: "DiscrepancyTypes",
      header: "Discrepancy Types",
      filter: true,
      filterMatchMode: "tokenAny",
      style: W.l,
      body: tokenBodyFor("DiscrepancyTypes"),
      bodyStyle: WRAP_PRELINE,
    },
    {
      field: "HygieneTypes",
      header: "Hygiene Types",
      filter: true,
      filterMatchMode: "tokenAny",
      style: W.ss,
      body: tokenBodyFor("HygieneTypes"),
      bodyStyle: WRAP_PRELINE,
    },
    {
      field: "DiscrepancySeverity",
      header: "Severity",
      filter: true,
      filterMatchMode: "tokenAny",
      style: W.s,
      body: tokenBodyFor("DiscrepancySeverity"),
    },
    {
      field: "MatchConfidence",
      header: "Match %",
      sortable: true,
      style: W.s,
      filter: false,
      bodyStyle: { textAlign: "center" },
    },
    {
      field: "DataHygieneAlert",
      header: "Hygiene Alert",
      body: dataHygieneAlertBody,
      filter: true,
      filterMatchMode: "equals",
      style: W.xss,
      bodyStyle: { textAlign: "center" },
    },
    {
      field: "ManualReviewRequired",
      header: "Manual Review",
      body: manualReviewRequiredBody,
      filter: true,
      filterMatchMode: "equals",
      style: W.xss,
      bodyStyle: { textAlign: "center" },
    },
  ];

  const DEFAULT_HIDDEN_COLUMNS = new Set(["Brand", "SRISRC", "CompAssetId"]);
  const [visibleColumns, setVisibleColumns] = useState<string[]>(
    COLUMN_DEFS.map((def) => def.field).filter(
      (value) => !DEFAULT_HIDDEN_COLUMNS.has(value),
    ),
  );

  type LatestDiscrepanciesResponse = Awaited<
    ReturnType<typeof apiService.metadataAuditComparer.listLatestDiscrepancies>
  >;

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      let cursor: string | undefined = undefined;
      let cursorMatchConfidence: number | undefined = undefined;
      const allRows: MetadataAuditComparerRow[] = [];
      const hasConfidenceRange =
        queryFilters.minMatchConfidence !== undefined ||
        queryFilters.maxMatchConfidence !== undefined;
      const sortByParam: "assetKey" | "MatchConfidence" = hasConfidenceRange
        ? "MatchConfidence"
        : sortBy;

      while (true) {
        const pageResults: LatestDiscrepanciesResponse =
          await apiService.metadataAuditComparer.listLatestDiscrepancies({
            hasDiscrepancy:
              queryFilters.hasDiscrepancy === null
                ? undefined
                : queryFilters.hasDiscrepancy,
            sortBy: sortByParam,
            sortDir,
            minMatchConfidence: queryFilters.minMatchConfidence,
            maxMatchConfidence: queryFilters.maxMatchConfidence,
            limit: 500,
            startAfterAssetKey: cursor,
            startAfterMatchConfidence:
              sortByParam === "MatchConfidence"
                ? cursorMatchConfidence
                : undefined,
          });

        const normalizedRows: MetadataAuditComparerRow[] = pageResults.rows.map(
          (row: MetadataAuditComparerRow) => ({
            ...row,
            MCWriters: formatStringField(row.MCWriters),
            CompWriter: formatStringField(row.CompWriter),
            DiscrepancyTypes: formatStringField(row.DiscrepancyTypes),
          }),
        );

        allRows.push(...normalizedRows);

        if (!pageResults.nextStartAfterAssetKey) break;

        if (
          sortByParam === "MatchConfidence" &&
          pageResults.nextStartAfterMatchConfidence === undefined
        ) {
          break;
        }

        cursor = pageResults.nextStartAfterAssetKey;
        if (sortByParam === "MatchConfidence") {
          cursorMatchConfidence = pageResults.nextStartAfterMatchConfidence;
        }
      }
      setRows(allRows);
    } catch (error) {
      setError(
        error instanceof Error ? error.message : "An unknown error occurred",
      );
    } finally {
      setLoading(false);
    }
  }, [
    queryFilters.hasDiscrepancy,
    queryFilters.minMatchConfidence,
    queryFilters.maxMatchConfidence,
    sortBy,
    sortDir,
  ]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  return (
    <div>
      <Card className="card-style">
        {error ? (
          <div>{error}</div>
        ) : loading ? (
          <ProgressSpinner className="comparer-progress-spinner" />
        ) : (
          <>
            <FilterBar
              queryFilters={queryFilters}
              setQueryFilters={setQueryFilters}
              filteredExport={exportFilteredCsv}
              visibleColumns={visibleColumns}
              setVisibleColumns={setVisibleColumns}
              discrepancyViewOptions={discrepancyViewOptions}
              columnDefs={COLUMN_DEFS}
              tableFilters={tableFilters}
              handleFieldFilter={handleFieldFilter}
              tokenFilterOptionsFields={tokenFilterOptionsFields}
              updateMatchConfidenceRange={updateMatchConfidenceRange}
              multiDrafts={multiDrafts}
              handleMultiDraftChange={handleMultiDraftChange}
              handleMultiAdd={handleMultiAdd}
              handleMultiRemove={handleMultiRemove}
              handleMultiKeyDown={handleMultiKeyDown}
              handleMultiClear={handleMultiClear}
              handleResetFilters={handleResetFilters}
            />
            <DataTable
              key={visibleColumns.join("|")}
              ref={dt}
              exportFilename={dtName}
              value={rows}
              filters={tableFilters}
              onFilter={(e) => setTableFilters(e.filters)}
              size="small"
              paginator
              rows={PAGE_SIZE}
              paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
              currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
              showGridlines
              scrollable
              scrollHeight={FULL_PAGE_TABLE_SCROLL_HEIGHT}
              stripedRows
              removableSort
              sortMode="single"
              sortField={sortBy}
              sortOrder={sortDir === "asc" ? 1 : -1}
              onSort={(e) => {
                const field = e.sortField as string | undefined;
                const order = e.sortOrder === -1 ? "desc" : "asc";

                const nextSortBy =
                  field === "MatchConfidence" ? "MatchConfidence" : "assetKey";

                setSortBy(nextSortBy);
                setSortDir(order);
              }}
              tableStyle={{
                tableLayout: "fixed",
                fontSize: "0.8rem",
              }}
              className="custom-datatable-paginator"
              rowClassName={severityRowClassNames}
            >
              {COLUMN_DEFS.filter((def) =>
                visibleColumns.includes(def.field),
              ).map((def) => (
                <Column
                  key={def.field}
                  field={def.field}
                  header={def.header}
                  sortable={def.sortable}
                  filter={def.filter}
                  filterMatchMode={def.filterMatchMode}
                  body={def.body}
                  style={def.style}
                  bodyStyle={def.bodyStyle}
                  showFilterMenu={false}
                  showClearButton={false}
                  showFilterMatchModes={false}
                  showFilterOperator={false}
                />
              ))}
            </DataTable>
          </>
        )}
      </Card>
    </div>
  );
};

export default MetadataAuditComparer;
